import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { Users, Activity, Clock } from 'lucide-react';

import BottomTabNavigation from '../../components/navigation/BottomTabNavigation';
import Icon from '../../components/AppIcon';
import DependentUserCard from './components/DependentUserCard';

import ActivityTimelineEntry from './components/ActivityTimelineEntry';
import AlertNotificationBanner from './components/AlertNotificationBanner';
import EmptyDependentsState from './components/EmptyDependentsState';
import AlertDetailsModal from './components/AlertDetailsModal';

import UpgradeCTA from '../../components/UpgradeCTA';
import { caregiverService } from '../../services/caregiverService';
import { alertService } from '../../services/alertService';
import subscriptionService from '../../services/subscriptionService';
import { logger } from '../../utils/logger';
import CloseButton from '../../components/CloseButton';
import LogoutButton from '../../components/LogoutButton';


const CaregiverDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [managedProfiles, setManagedProfiles] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [error, setError] = useState('');
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [pendingAlerts, setPendingAlerts] = useState([]);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [caregiverLimit, setCaregiverLimit] = useState(0);

  useEffect(() => {
    loadDashboardData();
    loadSubscriptionData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    setError('');

    try {
      // Load managed profiles
      const { data: profiles, error: profilesError } = await caregiverService?.getManagedProfiles();
      if (profilesError) {
        setError(profilesError?.message || 'Failed to load managed profiles');
        logger?.error('Failed to load managed profiles', profilesError);
      } else {
        setManagedProfiles(profiles || []);
      }

      // Load recent activity
      const { data: activity, error: activityError } = await caregiverService?.getRecentActivity(10);
      if (activityError) {
        logger?.error('Failed to load recent activity', activityError);
      } else {
        setRecentActivity(activity || []);
      }

      // Load pending alerts
      const { data: alerts, error: alertsError } = await alertService?.getPendingAlerts({ limit: 10 });
      if (alertsError) {
        logger?.error('Failed to load pending alerts', alertsError);
      } else {
        setPendingAlerts(alerts || []);
      }
    } catch (err) {
      setError('An unexpected error occurred');
      logger?.error('Dashboard load error', err);
    } finally {
      setLoading(false);
    }
  };

  const loadSubscriptionData = async () => {
    const { data } = await subscriptionService?.getCurrentSubscription();
    setSubscription(data);
    const limit = data?.plan?.limits?.caregiver_connections_limit || 0;
    setCaregiverLimit(limit);
  };

  const handleViewDetails = (profile) => {
    setSelectedProfile(profile);
    // Navigate to detailed view or open modal
    // For now, just log the action
    logger?.info('View profile details', { profileId: profile?.id });
  };

  const handleAddDependent = () => {
    navigate('/user-relationship-management');
  };

  const getTotalDependents = () => managedProfiles?.length || 0;

  const getRecentCheckIns = () => {
    const last7Days = new Date();
    last7Days?.setDate(last7Days?.getDate() - 7);
    return recentActivity?.filter(a => new Date(a?.createdAt) >= last7Days)?.length || 0;
  };

  const getAlertsCount = () => {
    return pendingAlerts?.length || 0;
  };

  const isBasicPlan = subscription?.plan?.planType === 'basic';
  const isApproachingCaregiverLimit = managedProfiles?.length >= caregiverLimit * 0.8;

  const handleAcknowledgeAlert = async (alertId) => {
    const { error } = await alertService?.acknowledgeAlert(alertId);
    if (error) {
      logger?.error('Failed to acknowledge alert', error);
    } else {
      setPendingAlerts(prev => prev?.filter(a => a?.id !== alertId));
      setShowAlertModal(false);
    }
  };

  const handleResolveAlert = async (alertId, notes) => {
    const { error } = await alertService?.resolveAlert(alertId, notes);
    if (error) {
      logger?.error('Failed to resolve alert', error);
    } else {
      setPendingAlerts(prev => prev?.filter(a => a?.id !== alertId));
      setShowAlertModal(false);
    }
  };

  const handleViewAlertDetails = (alert) => {
    setSelectedAlert(alert);
    setShowAlertModal(true);
  };

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Caregiver Dashboard - MyBodyToday</title>
          <meta name="description" content="Manage and monitor health tracking for your dependent users" />
        </Helmet>
        <div className="min-h-screen bg-background flex items-center justify-center pb-20">
          <div className="flex flex-col items-center gap-4">
            <Icon name="Loader2" size={40} className="text-primary animate-spin" strokeWidth={2} />
            <p className="text-muted-foreground">Loading dashboard...</p>
          </div>
        </div>
        <BottomTabNavigation />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Caregiver Dashboard - MyBodyToday</title>
        <meta name="description" content="Manage and monitor health tracking for your dependent users" />
      </Helmet>

      <div className="min-h-screen bg-background pb-24">
        {/* Header with Close Button */}
        <div className="bg-card border-b border-border">
          <div className="max-w-7xl mx-auto px-2.5 py-2 sm:px-3 sm:py-2.5 md:px-4 md:py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Users className="w-6 h-6 text-indigo-600" />
                <h1 className="text-base sm:text-lg md:text-xl font-heading font-bold text-foreground">
                  Caregiver Dashboard
                </h1>
              </div>
              <div className="flex items-center gap-3">
                <CloseButton 
                  navigateTo="/dashboard" 
                  ariaLabel="Close caregiver dashboard"
                  onClose={() => navigate('/dashboard')}
                />
                <LogoutButton />
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 pb-24">
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-error/10 border border-error rounded-lg text-error text-sm">
              {error}
            </div>
          )}

          {/* Alert Notifications */}
          <AlertNotificationBanner 
            alerts={pendingAlerts}
            onAcknowledge={handleAcknowledgeAlert}
            onViewDetails={handleViewAlertDetails}
          />

          {/* Contextual Upgrade CTA - Caregiver Limit */}
          {isBasicPlan && caregiverLimit === 0 && managedProfiles?.length === 0 && (
            <div className="mb-6">
              <UpgradeCTA
                variant="default"
                context="feature-locked"
                featureName="Caregiver Connections"
              />
            </div>
          )}

          {/* Quick Stats */}
          {managedProfiles?.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <button
                onClick={() => navigate('/user-relationship-management')}
                className="bg-card rounded-xl border border-border p-6 hover:shadow-therapeutic-md transition-smooth text-left group"
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <Icon name="Users" size={24} strokeWidth={2} className="text-primary" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">Manage Dependents</h3>
                <p className="text-sm text-muted-foreground">Add or manage dependent user profiles</p>
              </button>

              <button
                onClick={() => navigate('/caregiver-analytics-dashboard')}
                className="bg-card rounded-xl border border-border p-6 hover:shadow-therapeutic-md transition-smooth text-left group"
              >
                <div className="w-12 h-12 rounded-lg bg-accent/10 flex items-center justify-center mb-4 group-hover:bg-accent/20 transition-colors">
                  <Icon name="BarChart3" size={24} strokeWidth={2} className="text-accent" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">View Analytics</h3>
                <p className="text-sm text-muted-foreground">Track engagement and symptom patterns</p>
              </button>

              <button
                onClick={() => navigate('/audit-trail-dashboard')}
                className="bg-card rounded-xl border border-border p-6 hover:shadow-therapeutic-md transition-smooth text-left group"
              >
                <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center mb-4 group-hover:bg-success/20 transition-colors">
                  <Icon name="Shield" size={24} strokeWidth={2} className="text-success" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">Audit Trail</h3>
                <p className="text-sm text-muted-foreground">View modification history and data integrity</p>
              </button>
            </div>
          )}

          {/* Main Content */}
          {managedProfiles?.length === 0 ? (
            <EmptyDependentsState onAddDependent={handleAddDependent} />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
              {/* Dependent Users Section */}
              <div className="lg:col-span-2">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl sm:text-2xl font-heading font-semibold text-foreground">
                    Dependent Users
                  </h2>
                  <span className="text-sm text-muted-foreground">
                    {managedProfiles?.length} {managedProfiles?.length === 1 ? 'person' : 'people'}
                  </span>
                </div>
                <div className="space-y-4">
                  {managedProfiles?.map((profile) => (
                    <DependentUserCard
                      key={profile?.id}
                      profile={profile}
                      onViewDetails={handleViewDetails}
                    />
                  ))}
                </div>
              </div>

              {/* Recent Activity Section */}
              <div className="lg:col-span-1">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl sm:text-2xl font-heading font-semibold text-foreground">
                    Recent Activity
                  </h2>
                </div>
                {recentActivity?.length === 0 ? (
                  <div className="bg-card rounded-xl border border-border p-6 text-center">
                    <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <Icon name="Clock" size={28} className="text-primary" strokeWidth={2} />
                    </div>
                    <h4 className="text-base font-heading font-semibold text-foreground mb-2">No Activity Yet</h4>
                    <p className="text-sm text-muted-foreground mb-4">
                      Activity from your dependents' check-ins will appear here once they start logging symptoms.
                    </p>
                    <ul className="text-xs text-muted-foreground space-y-1.5 text-left bg-muted/40 rounded-lg p-3">
                      <li className="flex items-start gap-2">
                        <Icon name="CheckCircle2" size={14} className="text-primary mt-0.5 flex-shrink-0" />
                        <span>Daily check-ins from dependents show here</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="CheckCircle2" size={14} className="text-primary mt-0.5 flex-shrink-0" />
                        <span>Symptom updates and severity changes</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Icon name="CheckCircle2" size={14} className="text-primary mt-0.5 flex-shrink-0" />
                        <span>Alerts when attention may be needed</span>
                      </li>
                    </ul>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {recentActivity?.map((activity) => (
                      <ActivityTimelineEntry
                        key={activity?.id}
                        activity={activity}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Medical Disclaimer */}
          <div className="mt-8 p-4 bg-muted/50 border border-border rounded-lg">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={20} className="text-muted-foreground flex-shrink-0 mt-0.5" strokeWidth={2} />
              <div>
                <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                  <strong>Medical Disclaimer:</strong> This dashboard is for tracking purposes only and does not provide medical advice, diagnosis, or treatment recommendations. Always consult qualified healthcare professionals for medical concerns.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <BottomTabNavigation />

      {/* Alert Details Modal */}
      {showAlertModal && (
        <AlertDetailsModal
          alert={selectedAlert}
          onClose={() => setShowAlertModal(false)}
          onAcknowledge={handleAcknowledgeAlert}
          onResolve={handleResolveAlert}
        />
      )}
    </>
  );
};

export default CaregiverDashboard;